import _ from "lodash";

import { HttpService } from "../shared";
import {
  ResourceFieldName,
  ResourceFieldValues,
  ResourceName,
  ResourceDefinition,
  ResourcesServerData,
  SubscriberResource,
} from "./bpResources.types";

enum ResponseType {
  Blob = "blob",
}

const webContentFieldMap = {
  color: ResourceFieldName.Color,
  tagline: ResourceFieldName.Tagline,
  "web-page-admin-email": ResourceFieldName.AdminEmail,
  "web-page-admin-phone": ResourceFieldName.AdminPhone,
  "web-page-message": ResourceFieldName.Message,
  "web-page-show-message": ResourceFieldName.ShowMessage,
  "web-page-show-admin": ResourceFieldName.ShowAdmin,
  "web-page-show-code": ResourceFieldName.ShowCode,
  "web-page-show-name": ResourceFieldName.ShowName,
};

const safetyFieldMap = {
  color: ResourceFieldName.Color,
  tagline: ResourceFieldName.Tagline,
  "sec-page-admin-email": ResourceFieldName.AdminEmail,
  "sec-page-admin-phone": ResourceFieldName.AdminPhone,
  "sec-page-message": ResourceFieldName.Message,
  "sec-page-show-message": ResourceFieldName.ShowMessage,
  "sec-page-show-admin": ResourceFieldName.ShowAdmin,
};

const BooleanResourceFields = [
  ResourceFieldName.ShowMessage,
  ResourceFieldName.ShowAdmin,
  ResourceFieldName.ShowCode,
  ResourceFieldName.ShowName,
];

const serverToAppFieldMap = { ...webContentFieldMap, ...safetyFieldMap };

const mapAppToServerKeys = (
  values: ResourceFieldValues,
  resource: ResourceName
): Partial<typeof serverToAppFieldMap> => {
  const map =
    resource === ResourceName.WebContent ? webContentFieldMap : safetyFieldMap;

  return _.transform(
    values,
    (acc, value, key) => {
      const mappedKey = _.findKey(map, (val) => val === key);
      acc[mappedKey] = value;
      return acc;
    },
    {}
  );
};

export class BpResourcesProvider {
  constructor(private http: HttpService, private baseUrl: string) {}

  private get url(): string {
    return `${this.baseUrl}/ssm/account`;
  }

  public getResourceDefinitions(
    subscriberId: string
  ): Promise<ResourceDefinition[]> {
    return this.http
      .get<ResourcesServerData>(`${this.url}/${subscriberId}/resource`)
      .then(({ data: { content } }) =>
        content.map(({ fields, name }) => ({
          name,
          fields: fields.map((field) => ({
            ...field,
            name: serverToAppFieldMap[field.name] || field.name,
          })),
        }))
      );
  }

  public getLogo(subscriberId: string): Promise<{ data: Blob }> {
    return this.http.get(
      `${this.url}/${subscriberId}/resource/sb-logo/preview`,
      {
        responseType: ResponseType.Blob,
      }
    );
  }

  public getResource(
    subscriberId: string,
    resource: ResourceName
  ): Promise<ResourceFieldValues> {
    return this.http
      .get<SubscriberResource>(
        `${this.url}/${subscriberId}/resource/${resource}`
      )
      .then(({ data: { fields } }) => {
        return _.reduce(
          fields,
          (acc, value, key: ResourceFieldName) => {
            const prop = serverToAppFieldMap[key] || key;
            acc[prop] = BooleanResourceFields.includes(prop)
              ? value === "true"
              : value;
            if (key === "color") {
              acc[key] = value.toUpperCase();
            }
            return acc;
          },
          {}
        ) as ResourceFieldValues;
      });
  }

  public updateResource(
    subscriberId: string,
    resource: ResourceName,
    values: ResourceFieldValues
  ): Promise<void> {
    return this.http.put(
      `${this.url}/${subscriberId}/resource/${resource}`,
      mapAppToServerKeys(values, resource)
    );
  }

  public async updateLogo(
    subscriberId: string,
    logoObjectURL: string
  ): Promise<void> {
    let data;

    if (logoObjectURL) {
      data = (
        await this.http.get(logoObjectURL, {
          responseType: ResponseType.Blob,
        })
      ).data;
    }

    const formData = new FormData();
    formData.append("file", data || null);

    return this.http.post(
      `${this.url}/${subscriberId}/resource/sb-logo`,
      formData
    );
  }

  public getPreview(
    subscriberId: string,
    resource: ResourceName,
    values: ResourceFieldValues
  ) {
    return this.http.get(
      `${this.url}/${subscriberId}/resource/${resource}/preview`,
      { params: mapAppToServerKeys(values, resource) }
    );
  }
}
